import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, cn, fmtDate } from "../lib/api";
import { Card, Skeleton } from "./ui";
import MonthSelect, { currentMonth, monthLabel } from "./MonthSelect";

const PREVIEW = 6;

/** Admin home-screen card: who came on match days in a month, and who didn't. */
export default function AttendanceCard() {
  const nav = useNavigate();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("came");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    setShowAll(false);
    api.get("/stats/attendance", { params: { month } })
      .then(({ data }) => alive && setData(data))
      .catch((err) => alive && setError(apiError(err)));
    return () => { alive = false; };
  }, [month]);

  const list = data ? data[tab] : [];
  const visible = showAll ? list : list.slice(0, PREVIEW);

  return (
    <div className="px-5 mt-6" data-testid="attendance-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">Attendance • Admin</div>
        <MonthSelect value={month} onChange={setMonth} activeMonths={data?.months || []} testid="attendance-month" />
      </div>

      <Card className="p-4">
        {error ? (
          <div className="text-sm text-red-500">{error}</div>
        ) : !data ? (
          <Skeleton className="h-32" />
        ) : data.match_days.length === 0 ? (
          <div className="text-center py-3">
            <div className="text-sm font-semibold">No match days in {monthLabel(month)}</div>
            <div className="text-xs text-ink-muted mt-1">
              See who came and who didn't each month. A poll shows up here once its date has passed or a match is scored for it.
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat value={data.match_days.length} label="Match days" />
              <Stat value={data.came.length} label="Came" tone="text-brand" />
              <Stat value={data.missed.length} label="Didn't come" tone="text-red-500" />
            </div>

            <div className="flex gap-1.5 overflow-x-auto no-scrollbar mt-3 pb-1">
              {data.match_days.map((d) => (
                <button
                  key={d.poll_id}
                  onClick={() => nav(`/polls/${d.poll_id}`)}
                  className="shrink-0 px-2.5 py-1.5 rounded-xl bg-black/[0.04] text-xs text-left"
                  title={d.title}
                >
                  <div className="font-semibold">{fmtDate(d.date).replace(/ \d{4}$/, "")}</div>
                  <div className="text-ink-muted">{d.count} came</div>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-1 p-1 bg-black/[0.04] rounded-2xl mt-3">
              {[["came", `Came (${data.came.length})`], ["missed", `Didn't come (${data.missed.length})`]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => { setTab(k); setShowAll(false); }}
                  className={cn("h-9 rounded-xl text-sm font-semibold", tab === k ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
                  data-testid={`attendance-tab-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>

            {list.length === 0 ? (
              <div className="text-sm text-ink-muted text-center py-4">
                {tab === "came" ? "Nobody yet." : "Everyone came at least once 🎉"}
              </div>
            ) : (
              <div className="divide-y divide-black/[0.06] mt-2">
                {visible.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => nav(`/members/${p.id}`)}
                    className="w-full flex items-center justify-between gap-2 py-2.5 text-left"
                    data-testid={`attendance-row-${p.id}`}
                  >
                    <span className="font-semibold truncate">{p.name}</span>
                    <span className="text-xs text-ink-muted shrink-0 tabular-nums">
                      {tab === "came"
                        ? `${p.days} of ${data.match_days.length} day${data.match_days.length === 1 ? "" : "s"}`
                        : p.last_seen ? `Last came ${fmtDate(p.last_seen)}` : "Never came"}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {list.length > PREVIEW && (
              <button onClick={() => setShowAll(!showAll)} className="w-full text-sm font-semibold text-brand pt-2">
                {showAll ? "Show less" : `Show all ${list.length}`}
              </button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function Stat({ value, label, tone }) {
  return (
    <div>
      <div className={cn("text-2xl font-extrabold tabular-nums leading-tight", tone)}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">{label}</div>
    </div>
  );
}
